import type { WorkspaceIntegrationType } from "@storyboard/web-client";
import {
  membershipIsDriving,
  membershipIsHandsFreeOff,
  membershipIsNotDriving,
  membershipIsOffline,
  membershipIsVolumeMuted,
} from "@/store/availabilityPredicates";
import { chainComparators, compareAsc, compareDesc } from "@/store/compare";
import {
  availabilityForMembership,
  feedHasUnread,
  firstAliasForFeed,
  formatLocation,
  groupForFeed,
  isPhotoFile,
  latestItemForFeed,
  membershipHasReadOrWrite,
  namedFileForContent,
  preferenceForFeed,
  transcriptionForContent,
} from "@/store/feedSelectors";
import { idx, rowsByIndex, store } from "@/store/store";
import type { DirectWsInvitation, Feed, MembershipAvailability, WorkspaceMembership } from "@/store/types";
import {
  DRIVER_SAMSARA_USERNAME_SEARCH_TAG_LABEL,
  DRIVER_SAMSARA_VEHICLE_NAME_SEARCH_TAG_LABEL,
  type DriverFilterConfig,
  type DriverSearchTarget,
  type DriverStatusFilter,
  type DriverVolumeFilter,
} from "@/types/driver";
import { knownIntegrationType } from "@/utils/integrationLabels";

/** Newest invitation per membership (the old DISTINCT ON ... ORDER BY createdAt DESC pick). */
export function latestInvitationForMembership(workspaceMembershipId: string): DirectWsInvitation | null {
  let latest: DirectWsInvitation | null = null;
  for (const invitation of rowsByIndex("directWsInvitation", idx.invitationsByMembership, workspaceMembershipId)) {
    if (latest === null || (invitation.createdAt ?? "") > (latest.createdAt ?? "")) latest = invitation;
  }
  return latest;
}

/** One alias feed per membership (invited-but-not-logged-in users may have none synced locally). */
function primaryAliasFeedForMembership(workspaceMembershipId: string): Feed | null {
  let primary: Feed | null = null;
  for (const feed of rowsByIndex("feed", idx.feedsByAliasMembership, workspaceMembershipId)) {
    if (!feed.isAliasFeed) continue;
    if (primary === null || feed.id < primary.id) primary = feed;
  }
  return primary;
}

function membershipHasWrite(feedId: string, workspaceMembershipId: string): boolean {
  return rowsByIndex("permission", idx.permissionsByFeed, feedId).some(
    (p) => p.workspaceMembershipId === workspaceMembershipId && p.name === "write" && p.enabled,
  );
}

type MembershipIdBuckets = Pick<DriverFilterConfig, "unactivatedMembershipIds" | "personalDeviceMembershipIds">;

/** Exported for unit tests asserting status filters exclude unactivated memberships. */
export function statusFilterMatches(
  filter: DriverStatusFilter,
  availability: MembershipAvailability | null,
  membershipId: string,
  buckets: MembershipIdBuckets,
): boolean {
  // Unactivated members often have a null/stale heartbeat; exclude them from availability-based
  // buckets so filter rows match the cell label (Never logged in wins over Offline in the UI).
  if (
    filter !== "personal-device" &&
    filter !== "never-logged-in" &&
    buckets.unactivatedMembershipIds.includes(membershipId)
  ) {
    return false;
  }
  switch (filter) {
    case "driving":
      return membershipIsDriving(availability);
    case "not-driving":
      return membershipIsNotDriving(availability);
    case "hands-free-off":
      return membershipIsHandsFreeOff(availability);
    case "volume-muted":
      return membershipIsVolumeMuted(availability);
    case "offline":
      return membershipIsOffline(availability);
    case "personal-device":
      return buckets.personalDeviceMembershipIds.includes(membershipId);
    case "never-logged-in":
      return buckets.unactivatedMembershipIds.includes(membershipId);
  }
}

/**
 * The Device Volume column filter, evaluated against the same rounded percent the cell renders.
 * Non-muted means audible is true or unknown; "muted" is audible=false or a non-muted device
 * rounding to 0. Non-muted rows with a null volume fall into no percent bucket.
 */
export function volumeFilterMatches(filter: DriverVolumeFilter, availability: MembershipAvailability | null): boolean {
  const notMuted = availability?.mostRecentVolumeAudible !== false;
  const volume = availability?.mostRecentVolumeVolume;
  const percent = volume == null ? null : Math.round(volume * 100);
  switch (filter) {
    case "100":
      return notMuted && percent === 100;
    case "75-99":
      return notMuted && percent !== null && percent >= 75 && percent <= 99;
    case "50-74":
      return notMuted && percent !== null && percent >= 50 && percent <= 74;
    case "25-49":
      return notMuted && percent !== null && percent >= 25 && percent <= 49;
    case "1-24":
      return notMuted && percent !== null && percent >= 1 && percent <= 24;
    case "muted":
      return availability?.mostRecentVolumeAudible === false || (notMuted && percent === 0);
  }
}

function isOnlyNeverLoggedInFilter(config: DriverFilterConfig): boolean {
  return config.statusFilter.length === 1 && config.statusFilter[0] === "never-logged-in";
}

type SearchContext = {
  account: { name: string | null } | null;
  invitation: DirectWsInvitation | null;
  alias: string | null;
  groupName: string | null;
  availability: MembershipAvailability | null;
  membershipId: string;
};

function includesInsensitive(value: string | null | undefined, needle: string): boolean {
  return value != null && value.toLowerCase().includes(needle);
}

function membershipHasTagValueMatching(membershipId: string, tagId: string, needle: string): boolean {
  return rowsByIndex("workspaceMembershipTag", idx.membershipTagsByMembership, membershipId).some(
    (mt) => mt.tagId === tagId && mt.enabled && mt.tagValue.toLowerCase().includes(needle),
  );
}

function membershipHasLabeledTagMatching(membershipId: string, label: string, needle: string): boolean {
  return rowsByIndex("workspaceMembershipTag", idx.membershipTagsByMembership, membershipId).some((mt) => {
    if (!mt.enabled || !mt.tagValue.toLowerCase().includes(needle)) return false;
    const tag = store.get("tag", mt.tagId);
    return tag != null && tag.enabled && tag.label === label;
  });
}

function searchTargetMatches(target: DriverSearchTarget, needle: string, context: SearchContext): boolean {
  if (target.kind === "tag") {
    return membershipHasTagValueMatching(context.membershipId, target.tagId, needle);
  }
  switch (target.field) {
    case "name":
      return (
        includesInsensitive(context.account?.name, needle) ||
        includesInsensitive(context.invitation?.name, needle) ||
        includesInsensitive(context.invitation?.email, needle) ||
        includesInsensitive(context.invitation?.phoneNumber, needle)
      );
    case "alias":
      return includesInsensitive(context.alias, needle);
    case "group":
      return includesInsensitive(context.groupName, needle);
    case "location":
      return (
        includesInsensitive(context.availability?.mostRecentDeviceLocationPlaceName, needle) ||
        includesInsensitive(context.availability?.mostRecentDeviceLocationNearestAddressMunicipality, needle) ||
        includesInsensitive(context.availability?.mostRecentDeviceLocationNearestAddressRegion, needle) ||
        includesInsensitive(context.availability?.mostRecentDeviceLocationNearestAddressCountry, needle)
      );
    case "samsaraUsername":
      return membershipHasLabeledTagMatching(context.membershipId, DRIVER_SAMSARA_USERNAME_SEARCH_TAG_LABEL, needle);
    case "samsaraVehicleName":
      return membershipHasLabeledTagMatching(
        context.membershipId,
        DRIVER_SAMSARA_VEHICLE_NAME_SEARCH_TAG_LABEL,
        needle,
      );
  }
}

function filterConditionsPass(
  config: DriverFilterConfig,
  context: SearchContext & { availability: MembershipAvailability | null },
  groupId: string | null,
): boolean {
  const onlyNeverLoggedIn = isOnlyNeverLoggedInFilter(config);
  if (config.teamAliases.length > 0) {
    const aliasInTeam = context.alias != null && config.teamAliases.includes(context.alias);
    const unactivatedException =
      onlyNeverLoggedIn &&
      config.unactivatedMembershipIds.length > 0 &&
      config.unactivatedMembershipIds.includes(context.membershipId);
    if (!aliasInTeam && !unactivatedException) return false;
  }
  if (config.groupIds.length > 0 && (groupId == null || !config.groupIds.includes(groupId))) return false;
  if (
    config.statusFilter.length > 0 &&
    !config.statusFilter.some((f) => statusFilterMatches(f, context.availability, context.membershipId, config))
  ) {
    return false;
  }
  if (
    config.volumeFilter.length > 0 &&
    !config.volumeFilter.some((f) => volumeFilterMatches(f, context.availability))
  ) {
    return false;
  }
  if (
    config.dutyStatusFilter.length > 0 &&
    !config.dutyStatusFilter.some((status) => status === context.availability?.mostRecentDutyStatusStatus)
  ) {
    return false;
  }
  if (config.search && config.searchTargets.length > 0) {
    const needle = config.search.toLowerCase();
    if (!config.searchTargets.some((target) => searchTargetMatches(target, needle, context))) return false;
  }
  return true;
}

export type DriverRosterRow = {
  accountId: string | null;
  workspaceMembershipId: string;
  name: string | null;
  workspaceId: string | null;
  feedId: string | null;
  alias: string | null;
  feedPrefId: string | null;
  group: string | null;
  groupId: string | null;
};

function sortRosterRows(rows: DriverRosterRow[], sort: DriverFilterConfig["sort"]): void {
  const byMembership = (a: DriverRosterRow, b: DriverRosterRow) =>
    compareAsc(a.workspaceMembershipId, b.workspaceMembershipId);
  switch (sort) {
    case "alphaAsc":
      rows.sort(
        chainComparators(
          (a, b) => compareAsc((a.name ?? "").toLowerCase(), (b.name ?? "").toLowerCase()),
          byMembership,
        ),
      );
      break;
    case "alphaDesc":
      rows.sort(
        chainComparators(
          (a, b) => compareDesc((a.name ?? "").toLowerCase(), (b.name ?? "").toLowerCase()),
          byMembership,
        ),
      );
      break;
    default:
      rows.sort(chainComparators((a, b) => compareAsc(a.name, b.name), byMembership));
      break;
  }
}

type RosterParams = {
  workspaceId: string;
  membershipId: string;
  myCurrentWorkspaceMembershipId: string;
  filter: DriverFilterConfig;
};

/**
 * Whether the roster's filters consult availability.
 *
 * Gates both the roster's `wsMembershipAvailability` subscription and its per-membership
 * availability read, which have to agree: subscribing on every heartbeat would re-run the whole
 * roster scan, while reading a value no filter consults would put the collection back among the
 * compute's tracked reads without its writes ever refreshing the result.
 */
export function rosterNeedsAvailability(filter: DriverFilterConfig): boolean {
  return (
    filter.statusFilter.some((f) => f !== "personal-device" && f !== "never-logged-in") ||
    filter.volumeFilter.length > 0 ||
    filter.dutyStatusFilter.length > 0 ||
    (filter.search.length > 0 && filter.searchTargets.some((t) => t.kind === "column" && t.field === "location"))
  );
}

function buildRosterRow(
  membership: WorkspaceMembership,
  feed: Feed | null,
  params: RosterParams,
): DriverRosterRow | null {
  const account = membership.accountId != null ? (store.get("account", membership.accountId) ?? null) : null;
  const invitation = latestInvitationForMembership(membership.id);
  const alias = feed ? (firstAliasForFeed(feed.id)?.alias ?? null) : null;
  const availability = rosterNeedsAvailability(params.filter) ? availabilityForMembership(membership.id) : null;
  const group = feed ? groupForFeed(feed.id) : null;
  const preference = feed
    ? preferenceForFeed(feed.id, params.myCurrentWorkspaceMembershipId, { notifyingOnly: true })
    : null;

  const passes = filterConditionsPass(
    params.filter,
    { account, invitation, alias, groupName: group?.name ?? null, availability, membershipId: membership.id },
    group?.id ?? null,
  );
  if (!passes) return null;

  return {
    accountId: account?.id ?? null,
    workspaceMembershipId: membership.id,
    name: account?.name ?? invitation?.name ?? invitation?.email ?? invitation?.phoneNumber ?? null,
    workspaceId: feed?.workspaceId ?? membership.workspaceId,
    feedId: feed?.id ?? null,
    alias,
    feedPrefId: preference?.id ?? null,
    group: group?.name ?? null,
    groupId: group?.id ?? null,
  };
}

function standardDriverRoster(params: RosterParams): DriverRosterRow[] {
  const rows: DriverRosterRow[] = [];
  for (const membership of rowsByIndex("workspaceMembership", idx.membershipsByWorkspace, params.workspaceId)) {
    if (membership.status !== "active") continue;
    const aliasFeeds = rowsByIndex("feed", idx.feedsByAliasMembership, membership.id).filter((f) => f.isAliasFeed);
    if (aliasFeeds.length === 0) continue;

    // DISTINCT ON per membership preferred a row with a notifying preference (feedPrefId not null).
    let picked: DriverRosterRow | null = null;
    for (const feed of aliasFeeds) {
      if (!membershipHasReadOrWrite(feed.id, params.membershipId)) continue;
      const row = buildRosterRow(membership, feed, params);
      if (!row) continue;
      if (row.feedPrefId != null) {
        picked = row;
        break;
      }
      picked = picked ?? row;
    }
    if (picked) rows.push(picked);
  }
  sortRosterRows(rows, params.filter.sort);
  return rows;
}

function unactivatedDriverRoster(params: RosterParams): DriverRosterRow[] {
  const rows: DriverRosterRow[] = [];
  for (const membershipId of params.filter.unactivatedMembershipIds) {
    const membership = store.get("workspaceMembership", membershipId);
    if (!membership || membership.workspaceId !== params.workspaceId || membership.status !== "active") continue;
    const aliasFeed = primaryAliasFeedForMembership(membership.id);
    if (aliasFeed && !membershipHasReadOrWrite(aliasFeed.id, params.membershipId)) continue;
    const row = buildRosterRow(membership, aliasFeed, params);
    if (row) rows.push(row);
  }
  sortRosterRows(rows, params.filter.sort);
  return rows;
}

export const driverRosterCollections = [
  "workspaceMembership",
  "account",
  "directWsInvitation",
  "feed",
  "permission",
  "workspaceCommandAlias",
  "feedGroupMembership",
  "feedGroup",
  "feedPreference",
  "workspaceMembershipTag",
  "tag",
  "wsMembershipAvailability",
] as const;

/** Stays a compute pipeline: dynamic filters, Fuse-style search targets, and bespoke sorts. */
export function computeDriverRoster(params: RosterParams): DriverRosterRow[] {
  if (isOnlyNeverLoggedInFilter(params.filter) && params.filter.unactivatedMembershipIds.length > 0) {
    return unactivatedDriverRoster(params);
  }
  return standardDriverRoster(params);
}

export type DriverVolatileRow = {
  feedId: string;
  aliasMembershipId: string | null;
  recentMessage: string | null;
  /** Whether the latest message is a photo with no transcription, so `recentMessage` is only its file name. */
  recentMessagePhoto: boolean;
  unread: boolean | null;
  /** The feed's latest driver message, whose content `recentMessage` reads; null when the driver has sent none. */
  latestItemId: string | null;
  latestItemCreatedAt: string | null;
  location: string | null;
  volume: number | null;
  country: string | null;
  stateProvince: string | null;
  calculatedAvailability: string | null;
  offline: boolean | null;
  mostRecentHandsFreeStatusEnabled: boolean | null;
  mostRecentHandsFreeStatusUpdatedAt: string | null;
  mostRecentVolumeAudible: boolean | null;
  mostRecentDrivingStatus: string | null;
  mostRecentDutyStatusStatus: string | null;
  mostRecentDutyStatusChangedAt: string | null;
  mostRecentDrivingStatusChangedAt: string | null;
  membershipAvailabilityUpdatedAt: string | null;
  latitude: number | null;
  longitude: number | null;
  /** When the location was recorded, the availability row's ISO string; every surface judges staleness from it. */
  locationUpdatedAt: string | null;
  /** The ELD the location came from; null for the phone. */
  locationSource: WorkspaceIntegrationType | null;
  /** The place name the location's source gave, e.g. "Amarillo, TX"; null when it gave none. */
  locationPlaceName: string | null;
};

export const driverVolatileCollections = [
  "feed",
  "permission",
  "item",
  "transcription",
  "file",
  "wsMembershipAvailability",
] as const;

/** Stays a compute pipeline: per-feed latest-item, transcription, and availability merges. */
export function computeDriverVolatile({
  workspaceId,
  membershipId,
  language,
}: {
  workspaceId: string;
  membershipId: string;
  language: string;
}): DriverVolatileRow[] {
  {
    const rows: DriverVolatileRow[] = [];
    for (const feed of store.getAll("feed").values()) {
      if (feed.workspaceId !== workspaceId || !feed.isAliasFeed) continue;
      if (!membershipHasReadOrWrite(feed.id, membershipId)) continue;

      const latestItem = latestItemForFeed(feed.id, (item) => item.isFromAliasMember === true);
      const transcription = transcriptionForContent(latestItem?.contentId, language);
      const file = namedFileForContent(latestItem?.contentId);
      const availability = availabilityForMembership(feed.aliasMembershipId);

      rows.push({
        feedId: feed.id,
        aliasMembershipId: feed.aliasMembershipId,
        recentMessage: transcription?.transcriptionContent ?? file?.name ?? null,
        recentMessagePhoto: transcription == null && isPhotoFile(file),
        unread: feedHasUnread(feed.id),
        latestItemId: latestItem?.id ?? null,
        latestItemCreatedAt: latestItem?.createdAt ?? null,
        location: formatLocation(availability),
        volume: availability?.mostRecentVolumeVolume ?? null,
        country: availability?.mostRecentDeviceLocationNearestAddressCountry ?? null,
        stateProvince: availability?.mostRecentDeviceLocationNearestAddressRegion ?? null,
        calculatedAvailability: availability?.calculatedAvailability ?? null,
        offline: availability?.offline ?? null,
        mostRecentHandsFreeStatusEnabled: availability?.mostRecentHandsFreeStatusEnabled ?? null,
        mostRecentHandsFreeStatusUpdatedAt: availability?.mostRecentHandsFreeStatusUpdatedAt ?? null,
        mostRecentVolumeAudible: availability?.mostRecentVolumeAudible ?? null,
        mostRecentDrivingStatus: availability?.mostRecentDrivingStatus ?? null,
        mostRecentDutyStatusStatus: availability?.mostRecentDutyStatusStatus ?? null,
        mostRecentDutyStatusChangedAt: availability?.mostRecentDutyStatusChangedAt ?? null,
        mostRecentDrivingStatusChangedAt: availability?.mostRecentDrivingStatusChangedAt ?? null,
        membershipAvailabilityUpdatedAt: availability?.updatedAt ?? null,
        latitude: availability?.mostRecentDeviceLocationLatitude ?? null,
        longitude: availability?.mostRecentDeviceLocationLongitude ?? null,
        locationUpdatedAt: availability?.mostRecentDeviceLocationUpdatedAt ?? null,
        locationSource: knownIntegrationType(availability?.mostRecentDeviceLocationSource),
        locationPlaceName: availability?.mostRecentDeviceLocationPlaceName ?? null,
      });
    }
    return rows;
  }
}

function membershipAliasFeedsWithAccess(
  workspaceId: string,
  membershipId: string,
  teamAliases: string[] | undefined,
  access: (feedId: string, membershipId: string) => boolean = membershipHasReadOrWrite,
): Map<string, { feeds: Feed[]; notified: boolean }> {
  const byMembership = new Map<string, { feeds: Feed[]; notified: boolean }>();
  for (const membership of rowsByIndex("workspaceMembership", idx.membershipsByWorkspace, workspaceId)) {
    if (membership.status !== "active") continue;
    for (const feed of rowsByIndex("feed", idx.feedsByAliasMembership, membership.id)) {
      if (!feed.isAliasFeed || !access(feed.id, membershipId)) continue;
      if (teamAliases && teamAliases.length > 0) {
        const alias = firstAliasForFeed(feed.id);
        if (alias == null || !teamAliases.includes(alias.alias)) continue;
      }
      let entry = byMembership.get(membership.id);
      if (!entry) {
        entry = { feeds: [], notified: false };
        byMembership.set(membership.id, entry);
      }
      entry.feeds.push(feed);
      if (preferenceForFeed(feed.id, membershipId, { notifyingOnly: true })) entry.notified = true;
    }
  }
  return byMembership;
}

export const myDriverUnreadCountCollections = [
  "workspaceMembership",
  "feed",
  "permission",
  "workspaceCommandAlias",
  "feedPreference",
  "item",
] as const;

/** Stays a compute pipeline: per-membership unread aggregation over alias feeds. */
export function computeMyDriverUnreadCount({
  workspaceId,
  membershipId,
  teamAliases,
}: {
  workspaceId: string;
  membershipId: string;
  teamAliases: string[];
}): number {
  let count = 0;
  for (const { feeds } of membershipAliasFeedsWithAccess(workspaceId, membershipId, teamAliases).values()) {
    const hasNotifiedUnread = feeds.some(
      (feed) =>
        preferenceForFeed(feed.id, membershipId, { notifyingOnly: true }) != null && feedHasUnread(feed.id) === true,
    );
    if (hasNotifiedUnread) count++;
  }
  return count;
}

/**
 * Counts drivers for both tabs in one pass so switching between "My Drivers" and "All Drivers"
 * never recomputes: `allCount` is every permitted driver, `myCount` only those with a
 * notification-enabled feed preference.
 */
export const driverTotalCountCollections = [
  "workspaceMembership",
  "feed",
  "permission",
  "workspaceCommandAlias",
  "feedPreference",
] as const;

/** Stays a compute pipeline: per-membership counts for both driver tabs in one pass. */
export function computeDriverTotalCount({
  workspaceId,
  membershipId,
  teamAliases,
}: {
  workspaceId: string;
  membershipId: string;
  teamAliases: string[];
}): { allCount: number; myCount: number } {
  let allCount = 0;
  let myCount = 0;
  for (const { notified } of membershipAliasFeedsWithAccess(workspaceId, membershipId, teamAliases).values()) {
    allCount++;
    if (notified) myCount++;
  }
  return { allCount, myCount };
}

export type FleetMessageAudiences = {
  /** One channel per driver the sender may write to on the open team. */
  all: string[];
  /** The subset the sender is notified on — the roster's "My Drivers" tab. */
  mine: string[];
};

export const fleetMessageAudienceCollections = [
  "workspaceMembership",
  "feed",
  "permission",
  "workspaceCommandAlias",
  "feedPreference",
] as const;

/**
 * Channels behind the fleet-message pane's two whole-audience shortcuts, All Drivers and My Drivers.
 *
 * Counted per driver off the same pass as the roster's tab counts, so the pane's "(412)" is the roster's 412 rather
 * than a channel tally: a driver keyed to several team aliases is one recipient, addressed on the channel their
 * roster row names. Write access is what makes a driver addressable, so a channel the sender may only read is left
 * out of both counts — a narrower set than the roster's, which lists read access too.
 */
export function computeFleetMessageAudiences({
  workspaceId,
  membershipId,
  teamAliases,
}: {
  workspaceId: string;
  membershipId: string;
  teamAliases: string[];
}): FleetMessageAudiences {
  const all: string[] = [];
  const mine: string[] = [];
  for (const { feeds, notified } of membershipAliasFeedsWithAccess(
    workspaceId,
    membershipId,
    teamAliases,
    membershipHasWrite,
  ).values()) {
    const addressed =
      feeds.find((feed) => preferenceForFeed(feed.id, membershipId, { notifyingOnly: true })) ?? feeds[0];
    if (!addressed) continue;
    all.push(addressed.id);
    if (notified) mine.push(addressed.id);
  }
  return { all, mine };
}
